import { useMemo, useRef } from 'react';
import { sbEditable } from '@storyblok/storyblok-editable';
import type { StoryblokComponent } from 'storyblok-js-client';
import CampaignStatistics from 'components/organisms/campaign/CampaignStatistics';
import CampaignComparison from 'components/organisms/campaign/CampaignComparison';
import CampaignDownload from 'components/organisms/campaign/CampaignDownload';
import PardotPdfForm from 'components/blocks/pardot-pdf-form/PardotPdfForm';
import type { PardotPdfFormBlokData } from 'components/blocks/pardot-pdf-form/PardotPdfForm.data';
import type { ImageAsset } from 'util/storyblok/types/global.types';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import { getCampaignSectionTheme } from '@/data/campaign-theme';

type Blok<T extends string> = StoryblokComponent<T> & { use_accent_color?: boolean };
type Statistic = Blok<'campaign_statistic'> & {
  percentage?: number | string;
  description?: string;
};
type Comparison = Blok<'campaign_comparison_item'> & { from?: string; to?: string };

export function CampaignStatisticsBlock({
  blok,
}: {
  blok: Blok<'campaign_statistics'> & { label?: string; items?: Statistic[] };
}) {
  const ref = useRef<HTMLDivElement>(null);
  useThemeScrollBlock(ref, !!blok.use_accent_color);
  return (
    <div ref={ref} {...sbEditable(blok)}>
      <CampaignStatistics
        label={blok.label || 'Report highlights'}
        items={(blok.items || [])
          .filter((item) => item.component === 'campaign_statistic')
          .map((item) => ({
            id: item._uid,
            percentage: Number(item.percentage),
            description: item.description || '',
          }))}
      />
    </div>
  );
}

export function CampaignComparisonBlock({
  blok,
}: {
  blok: Blok<'campaign_comparison'> & { label?: string; items?: Comparison[] };
}) {
  const ref = useRef<HTMLDivElement>(null);
  useThemeScrollBlock(ref, !!blok.use_accent_color);
  return (
    <div ref={ref} {...sbEditable(blok)}>
      <CampaignComparison
        label={blok.label || 'Report transformation framework'}
        items={(blok.items || [])
          .filter((item) => item.component === 'campaign_comparison_item')
          .map((item) => ({ id: item._uid, from: item.from || '', to: item.to || '' }))}
      />
    </div>
  );
}

export function CampaignDownloadBlock({
  blok,
}: {
  blok: Blok<'campaign_download'> & {
    section_background?: { color?: string };
    use_cream_background?: boolean;
    title?: string;
    anchor_id?: string;
    report_cover?: ImageAsset;
    form?: (PardotPdfFormBlokData & Blok<'pardot_pdf_form'>)[];
  };
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Legacy cream is an alternate colour; the toggle still controls whether it is used.
  const background =
    blok.section_background?.color || (blok.use_cream_background ? '#f7f0e3' : undefined);
  const sectionTheme = useMemo(() => getCampaignSectionTheme(background), [background]);
  useThemeScrollBlock(ref, !!blok.use_accent_color, sectionTheme);
  const form = blok.form?.find((item) => item.component === 'pardot_pdf_form');
  const cover = blok.report_cover;
  const dimensions = cover?.filename?.match(/\/(\d+)x(\d+)\//);
  return (
    <div
      ref={ref}
      {...sbEditable(blok)}
      data-scroll-to={blok.anchor_id || 'campaign-report-download'}
    >
      <CampaignDownload
        id={blok.anchor_id || 'campaign-report-download'}
        title={blok.title || 'Download\nthe report'}
        image={
          cover?.filename
            ? {
                src: cover.filename,
                alt: cover.alt || '',
                width: Number(dimensions?.[1]) || 872,
                height: Number(dimensions?.[2]) || 1236,
              }
            : undefined
        }
      >
        {form ? (
          <PardotPdfForm layout="embedded" blok={form} />
        ) : (
          <p>Download form to be configured.</p>
        )}
      </CampaignDownload>
    </div>
  );
}
